import { useDebouncedCallback, useThrottledCallback } from "@react-hookz/web";
import first from "lodash/first";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { Dispatch, FunctionComponent, ReactNode, SetStateAction } from "react";
import { Events, scroller } from "react-scroll";

import { notEmpty } from "shared/lib/utils";

import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { useIntersection } from "scmp-app/lib/hooks";

import { ItemNamePrefix } from "./consts";
import { getContentItemClassName, getTimelineItemClassName } from "./helpers";

type ContextValue = {
  captureContentItemElement?: (dom: HTMLElement | null) => void;
  captureResetElement?: (dom: HTMLElement | null) => void;
  scrollToTimelineItem?: string;
  setScrollItem?: (entityUuid?: null | string) => void;
  setTimelineScrollContainerReference?: Dispatch<SetStateAction<HTMLDivElement | undefined>>;
};

type Props = {
  children: ReactNode;
};

const LiveArticleScrollContext = createContext<ContextValue>({});

export const LiveArticleScrollContextProvider: FunctionComponent<Props> = ({ children }) => {
  const { setScrollToContentItem } = useScrollToContent();
  const { scrollToTimelineItem, setScrollToTimelineItem, setTimelineScrollContainerReference } =
    useScrollToTimeline();
  const {
    captureContentItemElement,
    captureResetElement,
    contentItemIntersections,
    resetIntersections,
  } = useIntersectionDetector();

  const setScrollItem = useCallback(
    (entityUuid?: null | string) => {
      setScrollToContentItem(entityUuid ?? undefined);
      setScrollToTimelineItem(entityUuid ?? undefined);
    },
    [setScrollToContentItem, setScrollToTimelineItem],
  );

  useEffect(() => {
    if (resetIntersections.filter(intersection => intersection.intersectionRatio > 0).length === 0)
      return;
    setScrollItem?.(undefined);
  }, [resetIntersections, setScrollItem]);

  useEffect(() => {
    const intersection = first(
      contentItemIntersections
        .filter(intersection => intersection.intersectionRatio > 0)
        .toSorted((a, b) => b.intersectionRatio - a.intersectionRatio),
    );
    if (!intersection) return;

    const className = first(
      [...intersection.target.classList.values()].filter(name =>
        name.startsWith(ItemNamePrefix.Content),
      ),
    );
    if (!className) return;

    const [_, entityUuid] = className.split("_");
    if (!entityUuid) return;

    setScrollToTimelineItem(entityUuid);
  }, [contentItemIntersections, setScrollItem, setScrollToContentItem, setScrollToTimelineItem]);

  return (
    <LiveArticleScrollContext.Provider
      value={{
        captureContentItemElement,
        captureResetElement,
        scrollToTimelineItem,
        setScrollItem,
        setTimelineScrollContainerReference,
      }}
    >
      {children}
    </LiveArticleScrollContext.Provider>
  );
};

export const useLiveArticleScrollContext = () => useContext(LiveArticleScrollContext);

const useScrollToContent = () => {
  const { appBarHeight } = useAppBarStickyHeightValue();
  const [scrollToContentItem, setScrollToContentItem] = useState<string>();

  const debouncedSetScrollToContentItem = useDebouncedCallback(setScrollToContentItem, [], 200);

  useEffect(() => {
    if (!notEmpty(scrollToContentItem)) return;

    const target = getContentItemClassName(scrollToContentItem);
    if (!target) return;

    scroller.scrollTo(target, {
      duration: 800,
      isDynamic: true,
      offset: -appBarHeight - 25,
      smooth: true,
    });
  }, [appBarHeight, scrollToContentItem]);

  return { setScrollToContentItem: debouncedSetScrollToContentItem };
};

const useScrollToTimeline = () => {
  const [timelineScrollContainerReference, setTimelineScrollContainerReference] =
    useState<HTMLDivElement>();
  const [scrollToTimelineItem, setScrollToTimelineItem] = useState<string>();

  const debouncedSetScrollToTimelineItem = useDebouncedCallback(setScrollToTimelineItem, [], 200);

  useEffect(() => {
    if (!notEmpty(scrollToTimelineItem) || !notEmpty(timelineScrollContainerReference)) return;

    const target = getTimelineItemClassName(scrollToTimelineItem);
    if (!target) return;

    scroller.scrollTo(target, {
      container: timelineScrollContainerReference,
      duration: 500,
      smooth: true,
    });
  }, [scrollToTimelineItem, timelineScrollContainerReference]);

  return {
    scrollToTimelineItem,
    setScrollToTimelineItem: debouncedSetScrollToTimelineItem,
    setTimelineScrollContainerReference,
  };
};

const useIntersectionDetector = () => {
  const { captureIntersectionElement: captureResetElement, intersections: resetIntersections } =
    useIntersection({
      threshold: [0.1, 0.5, 1],
    });

  const {
    captureIntersectionElement: captureContentItemElement,
    intersections: contentItemIntersections,
    setShouldDisable,
  } = useIntersection({
    rootMargin: "-10% 0px -70% 0px",
    threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
  });

  const throttledSetShouldDisable = useThrottledCallback(setShouldDisable, [setShouldDisable], 800);

  useEffect(() => {
    Events.scrollEvent.register("begin", to => {
      if (!to?.startsWith(ItemNamePrefix.Content)) return;
      throttledSetShouldDisable(true);
    });
    Events.scrollEvent.register("end", to => {
      if (!to?.startsWith(ItemNamePrefix.Content)) return;
      throttledSetShouldDisable(false);
    });
    return () => {
      Events.scrollEvent.remove("begin");
      Events.scrollEvent.remove("end");
    };
  }, [throttledSetShouldDisable]);

  return {
    captureContentItemElement,
    captureResetElement,
    contentItemIntersections,
    resetIntersections,
  };
};
